import _extends from "@swc/helpers/src/_extends.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import React, { createContext, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { LoadableComponentContextProvider } from '@wix/da-hooks/pkg/useLoadableComponent';
import useEditor from '../DaEditorWrapper/hooks/useEditor';
// TODO: Move this type to CommentEditor instead?
import { CommentableTypes } from '../Comment/redux/types';
import { CommentEditorSource } from './redux/types';
import { useCommentEditor } from './hooks/useCommentEditor';
import { useCommentDaEditorReadyHandler } from './hooks/useCommentDaEditorReadyHandler';
import DraftCommentAutoTrigger from './DraftCommentAutoTrigger';
import { getRequiresFallback } from '../DaEditorWrapper/redux/selectors';
export const EditorContext = /*#__PURE__*/ createContext({
    api: null,
    state: null,
    toolbars: null,
    isReady: false,
    overrideEditorState: ()=>{},
    props: {}
});
const EditorContextProvider = (_param)=>{
    var { children , onEditorReady  } = _param, restOptions = _object_without_properties(_param, [
        "children",
        "onEditorReady"
    ]);
    const [isReady, setIsReady] = useState(false);
    const handleEditorReady = useCallback((toolbars, api)=>{
        setIsReady(true);
        onEditorReady && onEditorReady(toolbars, api);
    }, [
        onEditorReady
    ]);
    const editor = useEditor(_object_spread({
        onEditorReady: handleEditorReady
    }, restOptions));
    const memoizedEditorContext = useMemo(()=>({
            api: editor.editorApi,
            state: editor.editorState,
            toolbars: editor.editorToolbars,
            props: editor.editorProps,
            overrideEditorState: editor.overrideEditorState,
            isReady
        }), [
        editor,
        isReady
    ]);
    return /*#__PURE__*/ React.createElement(EditorContext.Provider, {
        value: memoizedEditorContext
    }, children);
};
export const CommentEditorContext = /*#__PURE__*/ createContext({
    // Mostly gibberish defaults
    editorId: '',
    commentableItemId: '',
    commentableType: CommentableTypes.COMMENT_USER,
    isBusy: false,
    isFocusRequested: false,
    shouldUseFallback: false,
    isFallbackEmpty: true,
    editorSource: CommentEditorSource.Independent,
    isExpanded: false,
    expandEditor: ()=>null,
    collapseEditor: ()=>null,
    setIsFallbackEmpty: ()=>null
});
const CommentEditorContextProvider = (_param)=>{
    var { children , commentBoxId , commentableItemId , commentableType , submitValidator  } = _param, useCommentEditorOptions = _object_without_properties(_param, [
        "children",
        "commentBoxId",
        "commentableItemId",
        "commentableType",
        "submitValidator"
    ]);
    const values = useCommentEditor(commentBoxId, commentableItemId, commentableType, useCommentEditorOptions);
    const memoizedCommentEditorContext = useMemo(()=>_object_spread_props(_object_spread({}, values), {
            editorId: commentBoxId,
            commentableItemId,
            commentableType
        }), [
        values,
        commentableItemId,
        commentableType,
        commentBoxId
    ]);
    return /*#__PURE__*/ React.createElement(CommentEditorContext.Provider, {
        value: memoizedCommentEditorContext
    }, children);
};
/**
 * Contains all the context providers we need for a comment editor implementation.
 *
 * Includes: `EditorContext`, `LoadableComponentContext`, and `CommentEditorContext`.
 */ export const CommentEditorProviders = (_param)=>{
    var { children , onEditorChange , onEditorInitialContentReady , onEditorReady , onFocus , onBlur , forceLoadOnMount , commentBoxId , autoFocus , submitValidator  } = _param, rest = _object_without_properties(_param, [
        "children",
        "onEditorChange",
        "onEditorInitialContentReady",
        "onEditorReady",
        "onFocus",
        "onBlur",
        "forceLoadOnMount",
        "commentBoxId",
        "autoFocus",
        "submitValidator"
    ]);
    const shouldUseFallback = useSelector(getRequiresFallback);
    const onCommentDaEditorReadyHandler = useCommentDaEditorReadyHandler(commentBoxId, autoFocus);
    const handleEditorReady = useCallback((toolbars, api)=>{
        onCommentDaEditorReadyHandler(toolbars, api);
        onEditorReady && onEditorReady(shouldUseFallback ? {} : toolbars, api);
    }, [
        onCommentDaEditorReadyHandler,
        onEditorReady,
        shouldUseFallback
    ]);
    const handleEditorChange = useCallback((newState)=>{
        onEditorChange && onEditorChange(newState);
    }, [
        onEditorChange
    ]);
    return /*#__PURE__*/ React.createElement(EditorContextProvider, {
        onEditorReady: handleEditorReady,
        onEditorChange: handleEditorChange,
        onEditorInitialContentReady: onEditorInitialContentReady,
        onFocus: onFocus,
        onBlur: onBlur
    }, /*#__PURE__*/ React.createElement(LoadableComponentContextProvider, {
        forceLoadOnMount: forceLoadOnMount
    }, /*#__PURE__*/ React.createElement(CommentEditorContextProvider, _extends({
        commentBoxId: commentBoxId,
        autoFocus: autoFocus,
        submitValidator: submitValidator
    }, rest), /*#__PURE__*/ React.createElement(DraftCommentAutoTrigger, {
        editorId: commentBoxId
    }), children)));
};
